import apiClient from './client';

/**
 * Programs and Bundles API
 */
const programsApi = {
  // Programs
  getPrograms: async () => {
    const response = await apiClient.get('/api/v1/programs');
    return response.data.data;
  },

  createProgram: async (programData) => {
    const response = await apiClient.post('/api/v1/programs', programData);
    return response.data;
  },

  updateProgram: async (programId, programData) => {
    const response = await apiClient.put(`/api/v1/programs/${programId}`, programData);
    return response.data;
  },

  deleteProgram: async (programId) => {
    const response = await apiClient.delete(`/api/v1/programs/${programId}`);
    return response.data;
  },

  getProgramSummary: async (programId) => {
    const response = await apiClient.get(`/api/v1/programs/${programId}/summary`);
    return response.data.data;
  },

  // Program details with parts and scenarios
  getProgramDetails: async (programId, scenarioId = null) => {
    const params = scenarioId ? { scenario_id: scenarioId } : {};
    const response = await apiClient.get(`/api/v1/programs/${programId}/details`, { params });
    return response.data;
  },

  // Get aggregated program forecast
  getProgramForecast: async (programId, scenarioId = null, scenarioName = null, granularity = 'annual') => {
    const params = { granularity };
    if (scenarioId) params.scenario_id = scenarioId;
    if (scenarioName) params.scenario_name = scenarioName;
    const response = await apiClient.get(`/api/v1/programs/${programId}/forecast`, { params });
    return response.data;
  },

  getProgramMarketForecasts: async (programId) => {
    const response = await apiClient.get(`/api/v1/programs/${programId}/market-forecasts`);
    return response.data;
  },

  getProgramActivity: async (programId, limit = 100) => {
    const response = await apiClient.get(`/api/v1/programs/${programId}/activity`, {
      params: { limit }
    });
    return response.data;
  },

  getProgramPriceLifecycle: async (programId, scenarioId = null) => {
    const params = scenarioId ? { scenario_id: scenarioId } : {};
    const response = await apiClient.get(`/api/v1/programs/${programId}/price-lifecycle`, { params });
    return response.data;
  },

  getProgramPartCandidates: async (programId, params = {}) => {
    const response = await apiClient.get(`/api/v1/programs/${programId}/part-candidates`, { params });
    return response.data;
  },

  linkPartsToProgram: async (programId, data) => {
    const response = await apiClient.post(`/api/v1/programs/${programId}/parts`, data);
    return response.data;
  },

  searchMarketForecasts: async (params = {}) => {
    const response = await apiClient.get('/api/v1/ihs/carlines', { params });
    return response.data;
  },

  linkMarketForecastToProgramParts: async (programId, data) => {
    const response = await apiClient.post(`/api/v1/programs/${programId}/market-forecasts/link`, data);
    return response.data;
  },

  linkMarketForecastBasisToProgram: async (programId, data) => {
    const response = await apiClient.post(`/api/v1/programs/${programId}/market-forecasts/basis`, data);
    return response.data;
  },

  unlinkMarketForecastBasisFromProgram: async (programId, coreMnemonic) => {
    const response = await apiClient.delete(`/api/v1/programs/${programId}/market-forecasts/basis/${coreMnemonic}`);
    return response.data;
  },

  unlinkMarketForecastFromProgramParts: async (programId, coreMnemonic, partIds = []) => {
    const response = await apiClient.delete(`/api/v1/programs/${programId}/market-forecasts/link/${coreMnemonic}`, {
      data: { part_ids: partIds }
    });
    return response.data;
  },

  // Create program scenario (auto-creates child scenarios)
  createProgramScenario: async (programId, data) => {
    const response = await apiClient.post(`/api/v1/programs/${programId}/scenarios`, data);
    return response.data;
  },

  // Update program part metadata
  updateProgramPart: async (programId, partId, data) => {
    const response = await apiClient.put(`/api/v1/programs/${programId}/parts/${partId}`, data);
    return response.data;
  },

  // Bundles
  getBundles: async () => {
    const response = await apiClient.get('/api/v1/bundles');
    return response.data.data;
  },

  createBundle: async (bundleData) => {
    const response = await apiClient.post('/api/v1/bundles', bundleData);
    return response.data;
  },

  // Bulk operations
  bulkAssignParts: async (partIds, programId, bundleId) => {
    const response = await apiClient.post('/api/v1/parts/bulk-assign', {
      part_ids: partIds,
      program_id: programId,
      bundle_id: bundleId
    });
    return response.data;
  },

  // Grouped view
  getOpportunitiesGrouped: async (groupBy = 'program') => {
    const response = await apiClient.get('/api/v1/opportunities/grouped', {
      params: { group_by: groupBy }
    });
    return response.data.data;
  }
};

export default programsApi;
